import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import ThemeButtonMenuItem from 'assets/components/button/themes/Theme.Button.MenuItem';
import Dropdown from 'assets/components/dropdown/Dropdown';
import ThemeDropdownPopup from 'assets/components/dropdown/themes/Theme.Dropdown.Popup';
import Icon, { iconStyles } from 'assets/components/icon/Icon';
import Theme from 'assets/themes/Theme.Common';
import { calculatePages } from 'assets/utils/data/Number';
import { useTheme } from '../../../hooks/theme/useTheme';
import DataMap from '../../dataMap/DataMap';
import RenderProps from '../../helpers/RenderProps';
import useSelect from './hooks/Select';
import { getLocales } from 'assets/locales/Locale';

export enum SelectInputStructure {
  formInput = 'formInput',
  inputSelection = 'inputSelection',
  input = 'input',
}
export type Verify = Utils.VerifyExtends<Component.Input.Select.Structure, typeof SelectInputStructure>;
export default function SelectInput<Name>(props: Component.Input.Select.Import<Name>) {
  const { animation, value, displayValue, getFilteredData, handleChange, search } = useSelect(props);
  const { lang } = getLocales();

  const offset = props.offset || 0;
  const limit = props.limit || 0;
  const count = calculatePages(limit, props.count || 0);
  //! TODO: Merging localProps with props doesnt work as expected.
  //! This is workaround for select component props with animateOut and animateIn functions.
  const extend = useTheme(
    SelectInputStructure,
    {
      ...props,
      animateOut: () => {
        animation.animateOut();
        if (props.animateOut) props.animateOut();
      },
      animateIn: () => {
        animation.animateIn();
        if (props.animateIn) props.animateIn();
      },
    },
    {
      animation: {
        formInput: {
          trigger: 'focus',
        },
      },
    }
  );

  return (
    <div
      {...extend(SelectInputStructure.formInput, true)}
      data-label={props.label}
      data-error={props.error || undefined}
      title={props.error ?? undefined}
      data-read-only={props.readOnly || undefined}
      data-disabled={props.disabled || undefined}
      children={
        <>
          {props.onAdd && (
            <Button
              class={ThemeButtonCircle}
              media={<Icon class={iconStyles.common.add} />}
              onClick={props.onAdd}
              htmlElementProps={{ title: lang.add }}
            />
          )}

          <input
            {...extend(SelectInputStructure.input)}
            type={'text'}
            id={props.name.toString()}
            name={'selectInput'}
            autoComplete="off"
            readOnly={props.readOnly}
            placeholder={props.placeholder}
            value={displayValue}
            onChange={search}
          />
          {RenderProps(props, 'icon')}
          {props.allowClear && (
            <Button
              class={ThemeButtonCircle}
              media={<Icon class={iconStyles.common.delete} />}
              onClick={props.onChange}
              data={{ [props.name as any]: props.isMultiselect ? [] : null } as any}
              htmlElementProps={{ title: lang.remove }}
              disabled={value.length === 0}
            />
          )}
          {animation.isVisible && (
            <Dropdown
              class={ThemeDropdownPopup}
              horizontalPosition="auto_fixed"
              verticalPosition="auto_fixed"
              animation={{ animate: animation.isIn }}
              render={
                <>
                  {!props.onOffsetChange ? null : (
                    <Box
                      style={{
                        padding: Theme.Size.S,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      render={
                        <>
                          {props.onOffsetChange && (
                            <>
                              <Button
                                class={ThemeButtonCircle}
                                style={{ marginLeft: 'auto' }}
                                media={<Icon class={iconStyles.common.left} />}
                                onClick={(value) => {
                                  if (props.onOffsetChange) props.onOffsetChange({ name: 'offset', value });
                                  //animation.animateIn();
                                }}
                                data={offset - 1}
                                disabled={offset < 1}
                                htmlElementProps={{ title: lang.prev }}
                              />
                              &nbsp;
                              {count > 1 && `${offset + 1}/${count}`}
                              &nbsp;
                              <Button
                                class={ThemeButtonCircle}
                                media={<Icon class={iconStyles.common.right} />}
                                onClick={(value) => {
                                  if (props.onOffsetChange) props.onOffsetChange({ name: 'offset', value });
                                  //animation.animateIn();
                                }}
                                data={offset + 1}
                                disabled={offset >= count - 1}
                                htmlElementProps={{ title: lang.next }}
                              />
                            </>
                          )}
                        </>
                      }
                    />
                  )}
                  <DataMap
                    data={getFilteredData()}
                    render={({ data }) => (
                      <Button
                        class={ThemeButtonMenuItem}
                        data={data.id}
                        key={data.id}
                        active={value.find((v) => v === data.id)}
                        onClick={handleChange}
                        disabled={data.disabled}
                        media={data.icon}
                        style={{ buttonText: {} }}
                        render={
                          <>
                            <span children={data.title} />
                            <span children={data.subtitle} />
                          </>
                        }
                        nonClickable={
                          props.onEdit &&
                          !data.disableEdit && (
                            <Button
                              class={ThemeButtonCircle}
                              style={{ marginLeft: 'auto', pointerEvents: 'all' }}
                              media={data.actionIcon || <Icon class={iconStyles.common.edit} />}
                              onClick={props.onEdit}
                              data={data.id}
                              htmlElementProps={{ title: data.actionTitle || lang.edit }}
                            />
                          )
                        }
                      />
                    )}
                  />
                </>
              }
            />
          )}
        </>
      }
    />
  );
}
